import type { BlogPost, ContentSection } from "@a7y/types";
import { docsPagePath } from "@a7y/types";
import {
  ALLOWED_IMAGE_MIME,
  DOCS_DRAFT_STORAGE_PREFIX,
  DRAFT_STORAGE_PREFIX,
  MAX_IMAGE_BYTES,
  PROD_BASE_URL,
  SEO_DESC_MAX,
  SEO_DESC_MIN,
  STAGING_BASE_URL,
} from "./constants";

/**
 * Pure helpers for the blog CMS editor (A7Y-2068). Everything here is
 * side-effect free and unit-tested in `utils.test.ts`.
 */

/** Kebab-case slug from a title: lowercase, strip accents/punctuation, collapse dashes. */
export function slugify(input: string): string {
  return input
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "") // strip combining diacritics
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Aggregate the distinct tag taxonomy across every post, sorted by descending
 * usage then alphabetically — seeds the TagPicker suggestions.
 */
export function aggregateTagTaxonomy(posts: BlogPost[]): string[] {
  const counts = new Map<string, number>();
  for (const post of posts) {
    for (const raw of post.tags ?? []) {
      const tag = raw.trim();
      if (!tag) continue;
      counts.set(tag, (counts.get(tag) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([tag]) => tag);
}

export type SeoLengthState = "empty" | "short" | "ok" | "long";

/** Classify a meta-description length against the SEO min/max guidance. */
export function seoLengthState(length: number): SeoLengthState {
  if (length === 0) return "empty";
  if (length < SEO_DESC_MIN) return "short";
  if (length > SEO_DESC_MAX) return "long";
  return "ok";
}

/**
 * Build the staging preview URL for a draft/scheduled document. Both the blog
 * preview route (A7Y-2067, `/blog/preview/:id`) and the docs preview route
 * (A7Y-4470, `/docs/preview/:id`) live on staging and 404 for a live document
 * and in prod. The two-argument overload is the original blog-only call
 * shape, kept for callers outside this ticket's footprint (`BlogListPage`);
 * every call site added by A7Y-4470 uses the three-argument, section-aware
 * form (C5).
 */
export function buildStagingPreviewUrl(id: string, token: string): string;
export function buildStagingPreviewUrl(
  section: ContentSection,
  id: string,
  token: string,
): string;
export function buildStagingPreviewUrl(a: string, b: string, c?: string): string {
  const [section, id, token]: [ContentSection, string, string] =
    c === undefined ? ["blog", a, b] : [a as ContentSection, b, c];
  return `${STAGING_BASE_URL}/${section}/preview/${id}?token=${encodeURIComponent(token)}`;
}

/**
 * Public URL of a live document (A7Y-4470 C5). Blog posts are served at
 * `/blog/posts/<slug>/` with a trailing slash — matching it avoids a redirect
 * hop on every click. Docs mirrors the
 * same trailing-slash shape via `docsPagePath`.
 */
export function buildLivePageUrl(section: ContentSection, slug: string): string {
  if (section === "docs") return `${PROD_BASE_URL}${docsPagePath(slug)}`;
  return `${PROD_BASE_URL}/blog/posts/${encodeURIComponent(slug)}/`;
}

/** @deprecated use `buildLivePageUrl("blog", slug)` — kept for callers outside this ticket's footprint. */
export function buildLivePostUrl(slug: string): string {
  return buildLivePageUrl("blog", slug);
}

/**
 * Flatten a Zod `flatten()` payload (`{ fieldErrors, formErrors }`) to a single
 * message per field. Mirrors the API's 400 `details` shape.
 */
export function mapZodFieldErrors(details: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  const fieldErrors = (details as { fieldErrors?: Record<string, string[]> })
    ?.fieldErrors;
  if (fieldErrors && typeof fieldErrors === "object") {
    for (const [key, msgs] of Object.entries(fieldErrors)) {
      if (Array.isArray(msgs) && msgs.length > 0) out[key] = msgs[0];
    }
  }
  return out;
}

export interface ImageCheckResult {
  ok: boolean;
  error?: string;
}

/** Client-side pre-check mirroring the API's MIME allow-list + 5 MB cap. */
export function isImageAllowed(file: {
  type: string;
  size: number;
}): ImageCheckResult {
  if (!ALLOWED_IMAGE_MIME.has(file.type)) {
    return {
      ok: false,
      error: `Unsupported image type: ${file.type || "unknown"}. Allowed: PNG, JPEG, WebP, SVG, GIF.`,
    };
  }
  if (file.size > MAX_IMAGE_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    return { ok: false, error: `Image is ${mb} MB — exceeds the 5 MB limit.` };
  }
  return { ok: true };
}

/** localStorage key for the per-post working-draft mirror. */
export function draftStorageKey(id: string | null | undefined): string {
  return `${DRAFT_STORAGE_PREFIX}${id ?? "new"}`;
}

/** localStorage key for the per-page docs working-draft mirror (A7Y-4470). */
export function docsDraftStorageKey(id: string | null | undefined): string {
  return `${DOCS_DRAFT_STORAGE_PREFIX}${id ?? "new"}`;
}
